import { getRmsUserTypeLabel, RMS_PROVIDER_ROLES } from '../../constants/userTypes';

/**
 * Who is on the non-clinical side of the BTH workspace (TDI-481): RMS
 * Support, and an Org. Admin who is not an assignable clinician. They may
 * read a withdrawn referral, read-only, to answer a family or the Trust. A
 * clinician never may, and a Clinical Org. Admin counts as a clinician.
 *
 * Mirrors isRmsNonClinicalProvider() in firestore.rules — keep in sync.
 */
export function isNonClinicalProvider(claims) {
  const userType = claims?.userType;
  if (userType === 'RMS Support') return true;
  return userType === 'RMS Org. Admin' && claims?.isAssignableClinician !== true;
}

/**
 * The role label shown on the Staff and Settings pages. A Clinical Org. Admin
 * reads "Org. Admin (Clinical)", as on the assessment staff page, so the two
 * kinds of Org. Admin can be told apart; clinical means isAssignableClinician
 * is true, the same test canTriage applies. Display only, never a role check.
 */
export function getRmsProviderRoleLabel(provider) {
  if (provider?.userType === RMS_PROVIDER_ROLES.ORG_ADMIN && provider?.isAssignableClinician === true) {
    return 'Org. Admin (Clinical)';
  }
  return getRmsUserTypeLabel(provider?.userType);
}

const personName = (p) => [p?.firstName, p?.lastName].filter(Boolean).join(' ').trim();

/**
 * Who may be named as sharing an ADHD triage decision under "Clinicians
 * involved" (TDI-703; SOP D.1). From the organisation's staff records:
 *
 *   - Clinicians whose stored specialism is adhd or both.
 *   - Clinical Org Admins (an Org Admin flagged isAssignableClinician), unless
 *     their stored specialism is asd.
 *
 * Never Non-clinical Org Admins or Support Users, whatever specialism is
 * stored, and never the signed-in clinician (they are the locked first row).
 * Invited staff not yet set up are listed, as before. Sorted by name, then
 * job title, so two people with the same name sit together and can be told
 * apart by the secondary text.
 *
 * @param {Array} staff - { clinicianId, firstName, lastName, userType,
 *   specialism, isAssignableClinician, jobTitle, email }
 * @param {string} currentUid - the signed-in clinician
 */
export function adhdDecisionRoster(staff, currentUid) {
  return (Array.isArray(staff) ? staff : [])
    .filter((p) => {
      if (!p?.clinicianId || p.clinicianId === currentUid) return false;
      const specialism = String(p.specialism || '').trim().toLowerCase();
      if (p.userType === RMS_PROVIDER_ROLES.CLINICIAN) return specialism === 'adhd' || specialism === 'both';
      if (p.userType === RMS_PROVIDER_ROLES.ORG_ADMIN) return p.isAssignableClinician === true && specialism !== 'asd';
      return false;
    })
    .sort((a, b) => (
      personName(a).toLowerCase().localeCompare(personName(b).toLowerCase())
      || String(a.jobTitle || '').toLowerCase().localeCompare(String(b.jobTitle || '').toLowerCase())
    ));
}

/** The grey text beside a name in the picker: job title, else email. */
export function rosterSecondaryText(person) {
  return String(person?.jobTitle || '').trim() || String(person?.email || '').trim();
}
